import { QueryResult, useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { showToast } from 'components/common/CustomToast';
import { GET_PARK_ZONE } from 'graphql/queries/getParkZone';
import { READ_PARKING_ZONE_CHARGING_STATIONS } from 'graphql/queries/readParkingZoneChargingStations';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import {
  Query,
  QueryParkZoneArgs,
  QueryReadParkingZoneChargingStationsArgs,
} from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';

const useParkingZoneWithEVCheck = (
  parkZoneUid: string,
  fetchPolicy?: WatchQueryFetchPolicy,
  fetchWithCharging = false
) => {
  const { setParkingConditions } = useParkingStore((state) => state);
  const navigation = useNavigation<ParkNavProps>();
  const isParkingZoneSet = parkZoneUid?.length > 0;
  const parkingZoneResult = useQuery<Query, QueryParkZoneArgs>(GET_PARK_ZONE, {
    variables: { uid: parkZoneUid },
    fetchPolicy: fetchPolicy || 'cache-first',
    skip: !isParkingZoneSet,
    errorPolicy: 'all',
    onCompleted: (data) => {
      const countryCode = data?.parkZone?.countryCode;
      setParkingConditions({
        parkingZoneCountryCode: countryCode ?? undefined,
      });
      useParkzoneStore.getState().setParkZoneCountry(countryCode);
    },
    onError: () => {
      showToast('error.getParkZoneFailed', 'error');
      navigation.navigate('park');
    },
  });

  const evChargingStationsResult = useQuery<
    Query,
    QueryReadParkingZoneChargingStationsArgs
  >(READ_PARKING_ZONE_CHARGING_STATIONS, {
    variables: { input: { parkingZoneUid: parkZoneUid } },
    fetchPolicy: fetchPolicy || 'cache-first',
    skip:
      !isParkingZoneSet ||
      !fetchWithCharging ||
      !parkingZoneResult.data?.parkZone?.parkingMethods?.evCharging,
    errorPolicy: 'all',
  });

  return {
    ...parkingZoneResult,
    data: {
      ...parkingZoneResult.data,
      parkZone: {
        ...parkingZoneResult.data?.parkZone,
        parkingZoneChargingStations:
          evChargingStationsResult.data?.readParkingZoneChargingStations,
      },
    },
    loading: evChargingStationsResult.loading || parkingZoneResult.loading,
  } as QueryResult<Query, QueryParkZoneArgs>;
};

export default useParkingZoneWithEVCheck;
